interface ProductStateStringRaw {
  facet_counts?: {
    facet_fields?: {
      platform_state_string?: string[];
    };
  };
}

interface ProductCvesRaw {
  facet_counts?: {
    facet_pivot?: {
      ['platform_state_string,cve_threatSeverity']?: {
        /**
         * Facet field name
         */
        field?: string;
        pivot?: {
          /**
           * CVE count of severity
           */
          count: number;
          /**
           * Severity type
           */
          value: 'Critical' | 'Important';
        }[];
        /**
         * Product name
         */
        value: string;
      }[];
    };
  };
}

export type ProductCvesData = {
  severity: {
    ['Important']: number;
    ['Critical']: number;
  };
};

type ProductName = string;

export type ProductCves = Record<ProductName, ProductCvesData>;

// Add new type for RHEL version hash table
export type RhelVersionHash = Record<ProductName, string[]>;

type ProductCveServiceParams = {
  /**
   * Endpoint for the Hydra CVE service.
   */
  endpoint?: string;
}

export class ProductCveService {
  private endpoint: NonNullable<ProductCveServiceParams['endpoint']>;

  public fromDate: Date;

  public toDate: Date;

  // Add new property for RHEL version hash table
  private rhelVersionHash: RhelVersionHash = {};

  /**
   * Initializes the ProductCveService.
   * @param args - The constructor arguments.
   * @param args.endpoint - The endpoint for the Hydra CVE service.
   */
  constructor(args?: ProductCveServiceParams) {
    const {
      endpoint,
    } = args ?? {};

    this.endpoint = endpoint ?? 'https://access.redhat.com/hydra/rest/search/kcs';

    // initialize date range
    this.toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(this.toDate.getDate() - 30);
    this.fromDate = fromDate;
  }

  /**
   * Retrieves CVE data for all products.
   * It fetches the product list and CVE data, then merges them.
   * @returns A promise that resolves to a `ProductCves` object.
   */
  async getCves(): Promise<ProductCves> {
    const productList = await this.getProductList();
    const cveData = await this.getCveData();

    // Initialize productCves with all products, setting default values
    const productCves: ProductCves = {};
    for (const product of productList) {
      productCves[product] = {
        severity: {
          Important: 0,
          Critical: 0
        }
      };
    }

    // Update with actual CVE data where available
    for (const item of cveData?.facet_counts?.facet_pivot?.['platform_state_string,cve_threatSeverity'] ?? []) {
      if (!productList.includes(item.value)) {
        continue;
      }
      const severity: ProductCvesData['severity'] = {
        Important: 0,
        Critical: 0,
      };
      for (const s of item?.pivot ?? []) {
        if (s.value === 'Important' || s.value === 'Critical') {
          severity[s.value] = s.count;
        }
      }
      productCves[item.value] = {
        severity
      };
    }

    return productCves;
  }

  /**
   * Fetches the list of products from the CVE service.
   * It also populates the `rhelVersionHash` with RHEL version information for applicable products.
   * @returns A promise that resolves to an array of product names.
   */
  async getProductList(): Promise<ProductName[]> {
    const url = new URL(this.endpoint);
    url.searchParams.append('q', '*:*');
    url.searchParams.append('facet', 'true');
    url.searchParams.append('facet.field', 'platform_state_string');
    url.searchParams.append('facet.limit', '-1');
    url.searchParams.append('rows', '0');

    const response = await fetch(url.toString()).then(res => res.json()) as ProductStateStringRaw;
    const stateStrings = response?.facet_counts?.facet_fields?.platform_state_string;

    const filteredNames = [];
    const rhelVersionHash: RhelVersionHash = {};

    if (stateStrings) {
      const namePattern = this._regexPatternByProductName([
        'Red Hat Enterprise Linux',
        'Red Hat OpenShift Container Platform',
        'Red Hat Ansible Automation Platform',
      ]);
      for (const [i, name] of stateStrings.entries()) {
        // Check if the index 'i' is even
        if (i % 2 !== 0) {
          continue;
        }
        /**
         * TODO: Temporary fix for treating AAP 2 as AAP 2.0 for RHEL 7
         */
        let _name = name;
        if (name === 'Red Hat Ansible Automation Platform 2|Fixed') {
          _name = 'Red Hat Ansible Automation Platform 2.0 for RHEL 7|Fixed';
        }
        const match = _name.match(namePattern);
        if (match) {
          filteredNames.push(_name);

          // Extract RHEL version if present
          const [_, productName, version, rhelVersion] = match;

          if (rhelVersion) {
            const key = `${productName}:${version}`;
            if (!rhelVersionHash[key]) {
              rhelVersionHash[key] = [];
            }
            if (!rhelVersionHash[key].includes(rhelVersion)) {
              rhelVersionHash[key].push(rhelVersion);
            }
          }
        }
      }
    }

    // Store the hash table as a class property for later use
    this.rhelVersionHash = rhelVersionHash;

    return filteredNames;
  }

  /**
   * Fetches raw CVE data from the Hydra service.
   * @returns A promise that resolves to the raw CVE data.
   * @throws An error if the fetch or parsing fails.
   */
  async getCveData(): Promise<ProductCvesRaw> {
    const url = new URL(this.endpoint);
    url.searchParams.append('facet', 'true');
    url.searchParams.append('facet.limit', '-1');
    url.searchParams.append('facet.range', '{!ex=ate}cve_publicDate');
    url.searchParams.append('facet.range.end', 'NOW');
    url.searchParams.append('facet.range.gap', '+1YEAR');
    url.searchParams.append('facet.range.start', 'NOW/YEAR-15YEARS');
    url.searchParams.append('fl', 'id,cve_threatSeverity,cve_publicDate,view_uri,allTitle,cve_details');
    url.searchParams.append('fq', `{!tag=ate}cve_publicDate:([${this.formateDate(this.fromDate)}T00:00:00.000Z TO ${this.formateDate(this.toDate)}T00:00:00.000Z]) AND documentKind:("Cve") AND language:("en")`);
    url.searchParams.append('hl', 'true');
    url.searchParams.append('hl.fl', 'lab_description');
    url.searchParams.append('hl.simple.pre', '<mark>');
    url.searchParams.append('hl.simple.post', '</mark>');
    url.searchParams.append('q', '*:*');
    url.searchParams.append('rows', '0');
    url.searchParams.append('sort', 'cve_publicDate desc,allTitle desc');
    url.searchParams.append('facet.field', 'platform_state_string');
    url.searchParams.append('facet.field', 'cve_threatSeverity');
    url.searchParams.append('facet.pivot', 'platform_state_string,cve_threatSeverity');

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error('ProductCveService: Failed to fetch CVE data');
    }

    try {
      return response.json() as ProductCvesRaw;
    } catch (e) {
      throw new Error('ProductCveService: Failed to parse CVE data');
    }
  }

  /**
   * Gets the RHEL versions associated with a product name.
   * @param name - The product name to look up.
   * @returns An array of RHEL versions, or undefined if not found.
   */
  public getRhelVariantsByProductName(name: ProductName): string[] | undefined {
    return this.rhelVersionHash[name];
  }

  /**
   * Formats a Date object into a 'YYYY-MM-DD' string.
   * @param date - The date to format.
   * @returns The formatted date string.
   */
  public formateDate(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // Helpers
  /**
   * Creates a regex pattern to match product names and extract version info.
   * @param name - An array of product names to include in the pattern.
   * @returns A RegExp object.
   */
  private _regexPatternByProductName(name: string[]) {
    return new RegExp(`^(${name.join('|')}) ((?:[0-9]|\\.)*)(?: for RHEL ((?:[0-9]|\\.)*)|)\\|Fixed`);
  }
}
